import { useEffect, useEffectEvent } from "react";
import { createDataError, errorLogger, ErrorSeverity } from "@/core/errors";

/**
 * Asks a store for what a component shows, when the component appears and whenever `key` changes: the id,
 * slug or state the load depends on. `load` itself is not a dependency, so it can be written inline and
 * always sees the current props. With no key (`null`, `undefined` or `false`) nothing is loaded.
 *
 * Stores load idempotently (a `Resource` ignores a key it holds), so this can be called freely. A failed load is
 * logged, never thrown into the render. For a page that must not show before its data, use `usePageData`.
 */
export const useLoad = (key: string | number | boolean | null | undefined, load: () => unknown, component?: string) => {
    const run = useEffectEvent(load);

    useEffect(() => {
        if (key === null || key === undefined || key === false) {
            return;
        }
        const failed = (error: unknown) =>
            errorLogger.logError(createDataError(error), ErrorSeverity.ERROR, { component, action: "load" });
        try {
            Promise.resolve(run()).catch(failed);
        } catch (error) {
            failed(error);
        }
    }, [key, component]);
};
